import React, { useState, useEffect, useRef } from 'react';
import './FileManager.css';

const FileManager = ({ username, apiUrl }) => {
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, fileId: null, fileName: '' });
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

  // Cargar archivos y estadisticas
  useEffect(() => {
    loadFiles();
    loadStats();
  }, [username]);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/api/files?username=${encodeURIComponent(username)}`);
      const data = await response.json();

      if (data.success) {
        setFiles(data.files || []);
      } else {
        setError(data.error || 'Error cargando archivos');
      }
    } catch (err) {
      console.error('Error loading files:', err);
      setError('Error de conexion al cargar archivos');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/files/stats?username=${encodeURIComponent(username)}`);
      const data = await response.json();

      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      uploadFile(selectedFiles[0]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      uploadFile(e.dataTransfer.files[0]);
    }
  };

  const uploadFile = async (file) => {
    // Validar tipo de archivo
    const allowedExtensions = ['.csv', '.pdf', '.xlsx', '.xls'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!allowedExtensions.includes(ext)) {
      setError('Solo se permiten archivos CSV, Excel (XLSX/XLS) y PDF');
      return;
    }

    // Validar tamano
    if (file.size > MAX_FILE_SIZE) {
      setError(`El archivo excede el limite de 50 MB (${(file.size / (1024 * 1024)).toFixed(2)} MB)`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);
    setSuccessMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('username', username);

      // Simular progreso de upload
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch(`${apiUrl}/api/files/upload`, {
        method: 'POST',
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = await response.json();

      if (data.success) {
        setSuccessMessage(`Archivo "${file.name}" subido exitosamente`);
        await loadFiles();
        await loadStats();

        // Limpiar input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setError(data.error || 'Error subiendo archivo');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Error de conexion al subir archivo');
    } finally {
      setUploading(false);
      setTimeout(() => {
        setUploadProgress(0);
        setSuccessMessage(null);
      }, 3000);
    }
  };

  const handleDownload = async (fileId, fileName) => {
    try {
      const response = await fetch(
        `${apiUrl}/api/files/${fileId}/download?username=${encodeURIComponent(username)}`
      );
      const data = await response.json();

      if (data.success && data.downloadUrl) {
        // Abrir URL de descarga en nueva ventana
        window.open(data.downloadUrl, '_blank');
      } else {
        setError(data.error || 'Error obteniendo enlace de descarga');
      }
    } catch (err) {
      console.error('Download error:', err);
      setError('Error de conexion al descargar archivo');
    }
  };

  const openDeleteModal = (fileId, fileName) => {
    setDeleteModal({ isOpen: true, fileId, fileName });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, fileId: null, fileName: '' });
  };

  const confirmDelete = async () => {
    const { fileId, fileName } = deleteModal;
    setDeleting(true);

    try {
      const response = await fetch(
        `${apiUrl}/api/files/${fileId}?username=${encodeURIComponent(username)}`,
        { method: 'DELETE' }
      );
      const data = await response.json();

      if (data.success) {
        setSuccessMessage(`Archivo "${fileName}" eliminado exitosamente`);
        await loadFiles();
        await loadStats();
      } else {
        setError(data.error || 'Error eliminando archivo');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError('Error de conexion al eliminar archivo');
    } finally {
      setDeleting(false);
      closeDeleteModal();
    }

    setTimeout(() => {
      setSuccessMessage(null);
    }, 3000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'pdf') {
      return (
        <svg className="file-icon pdf" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      );
    }
    // CSV y Excel usan el mismo icono
    return (
      <svg className="file-icon csv" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="8" y1="13" x2="16" y2="13" />
        <line x1="8" y1="17" x2="16" y2="17" />
        <line x1="12" y1="9" x2="12" y2="21" />
      </svg>
    );
  };

  return (
    <div className="file-manager">
      <div className="file-manager-header">
        <h3 className="section-title">Archivos de la Organizacion</h3>

        {stats && (
          <div className="file-stats">
            <div className="stat-item">
              <span className="stat-value">{stats.total_files}</span>
              <span className="stat-label">Archivos</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.csv_count || 0}</span>
              <span className="stat-label">CSV</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.excel_count || 0}</span>
              <span className="stat-label">Excel</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.pdf_count}</span>
              <span className="stat-label">PDF</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.total_size_formatted}</span>
              <span className="stat-label">Total</span>
            </div>
          </div>
        )}
      </div>

      {/* Zona de Upload */}
      <div
        className={`upload-zone ${dragActive ? 'drag-active' : ''} ${uploading ? 'uploading' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.pdf,.xlsx,.xls"
          onChange={handleFileSelect}
          disabled={uploading}
          style={{ display: 'none' }}
        />

        {uploading ? (
          <div className="upload-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
            <span className="progress-text">Subiendo... {uploadProgress}%</span>
          </div>
        ) : (
          <>
            <svg className="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <p className="upload-text">
              Arrastra archivos aqui o <span className="upload-link">haz clic para seleccionar</span>
            </p>
            <p className="upload-hint">Solo archivos CSV, Excel (XLSX/XLS) y PDF (max 50 MB)</p>
          </>
        )}
      </div>

      {/* Mensajes */}
      {error && (
        <div className="message error-message">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="message-close">×</button>
        </div>
      )}

      {successMessage && (
        <div className="message success-message">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <span>{successMessage}</span>
        </div>
      )}

      {/* Lista de archivos */}
      <div className="files-list">
        {loading ? (
          <div className="loading-files">
            <div className="loading-spinner"></div>
            <span>Cargando archivos...</span>
          </div>
        ) : files.length === 0 ? (
          <div className="no-files">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            <p>No hay archivos subidos</p>
            <p className="no-files-hint">Sube tu primer archivo CSV, Excel o PDF</p>
          </div>
        ) : (
          <table className="files-table">
            <thead>
              <tr>
                <th>Archivo</th>
                <th>Tipo</th>
                <th>Tamano</th>
                <th>Subido por</th>
                <th>Fecha</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <tr key={file.id}>
                  <td className="file-name-cell">
                    {getFileIcon(file.file_type)}
                    <span className="file-name-text" title={file.original_name}>
                      {file.original_name}
                    </span>
                  </td>
                  <td>
                    <span className={`file-type-badge ${file.file_type}`}>
                      {file.file_type.toUpperCase()}
                    </span>
                  </td>
                  <td>{file.file_size_formatted}</td>
                  <td className="uploaded-by-cell">{file.uploaded_by}</td>
                  <td>{formatDate(file.uploaded_at)}</td>
                  <td className="actions-cell">
                    <button
                      className="action-btn download-btn"
                      onClick={() => handleDownload(file.id, file.original_name)}
                      title="Descargar"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={() => openDeleteModal(file.id, file.original_name)}
                      title="Eliminar"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de confirmacion de eliminacion */}
      {deleteModal.isOpen && (
        <div className="delete-modal-overlay" onClick={closeDeleteModal}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 className="delete-modal-title">Eliminar archivo</h3>
            <p className="delete-modal-message">
              ¿Estas seguro de que deseas eliminar <strong>"{deleteModal.fileName}"</strong>?
            </p>
            <p className="delete-modal-warning">Esta accion no se puede deshacer.</p>
            <div className="delete-modal-actions">
              <button
                className="delete-modal-btn cancel-btn"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                className="delete-modal-btn confirm-btn"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileManager;
